import { FC, useState, useEffect } from 'react';

import { Stack, Box, Typography } from '@mui/material';

import { IPaged } from 'types';
import { CasinoService, IGame } from 'api/casino';

import Slider from 'components/common/Slider';
import GameCard from 'components/sections/GameCard';
import AspectRatio from 'components/common/AspectRatio';
import NextArrow from 'components/common/Slider/NextArrow';
import PrevArrow from 'components/common/Slider/PrevArrow';

import useScreen from 'hooks/useScreen';
import useTranslates from 'utils/translate';

interface PopularGamesProps {
  categoryId?: number;
  selectedPopularGames?: boolean;
  onSelectedPopularGames?: () => void;
}

const sx = {
  root: ({ isMobile }) => ({
    my: 2,
    mx: isMobile ? -3 : 0,
    p: isMobile ? 2 : 3,
    borderRadius: !isMobile && '20px',
    bgcolor: 'background.paper',
  }),
};

const PopularGames: FC<PopularGamesProps> = ({ categoryId }) => {
  const { isMobile, isTablet } = useScreen();
  const { translate } = useTranslates();
  const [games, setGames] = useState<IPaged<IGame>>({
    data: [],
    count: 0,
  });

  useEffect(() => {
    CasinoService.getGames({
      page: 1,
      size: 50,
      category: categoryId,
      orderKey: 'playCount',
    }).then((games) => {
      setGames(games);
    });
  }, [categoryId]);

  if (!games?.count) {
    return null;
  }

  const count = isTablet ? 4 : 6;
  const slidesToShow = games?.data?.length >= count ? count : games?.data?.length;

  return (
    <Box sx={sx.root({ isMobile })}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography fontSize={isMobile ? 12 : 18} fontWeight={600}>
          {translate('popular_games')}
        </Typography>
      </Box>
      {isMobile ? (
        <Stack
          className="hideScroll"
          direction="row"
          gap={1}
          style={{
            overflowX: 'auto',
          }}
        >
          {games?.data?.map((item) => (
            <Box key={item.id} minWidth="150px" height="113px">
              <GameCard game={item} height="100%" />
            </Box>
          ))}
        </Stack>
      ) : (
        <Slider
          infinite={false}
          slidesToShow={slidesToShow}
          slidesToScroll={slidesToShow}
          nextArrow={
            <NextArrow hideIfNotSlide sx={{ right: -24, left: 'auto', bottom: 'auto', top: 'calc(50% - 20px)' }} />
          }
          prevArrow={
            <PrevArrow hideIfNotSlide sx={{ left: -24, right: 'auto', bottom: 'auto', top: 'calc(50% - 20px)' }} />
          }
        >
          {games?.data?.map((item) => (
            <AspectRatio key={item.id} divide="4:3" spacing={1}>
              <GameCard game={item} height="100%" />
            </AspectRatio>
          ))}
        </Slider>
      )}
    </Box>
  );
};

export default PopularGames;
